import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { routes } from 'constants/config';
import { ElementRenderer } from 'components/Elements';
import {
  BlankSlate,
  Description,
  Heading,
} from 'components/_shared/blank_slate';
import { SavedElementTypes } from 'constants/savedElements';
import request from 'utils/request_ajax';
import { useFetch } from 'hooks';
import errorSrc from 'images/error.svg';
import { Button, ButtonGroup, Card, Spinner } from 'ui';
import DiscardChangesButton from './DiscardChangesButton';
import ElementInfo from './ElementInfo';
import PublishChangesButton from './PublishChangesButton';
import MoreButton from './MoreButton';

const TRANSITION_TIMEOUT = { enter: 500, exit: 300 };
const ERRORS = {
  DELETE: 'DELETE',
  PUBLISH: 'PUBLISH',
};

const ElementEditorRoot = ({ history, match, elementId }) => {
  const editing = match.path.includes('/edit');
  const [element, setElement] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const initialFetch = useFetch(`/content/library/elements/${elementId}`);

  // storing the response data in a dedicated state so that it can be mutated
  // via handleContentChange
  useEffect(() => setElement(initialFetch.data), [initialFetch.data]);

  function handleDelete() {
    setError(null);
    setIsDeleting(true);

    request
      .delete(routes.SAVED_ELEMENT_CONTENT(element.id))
      .then(() => {
        history.replace('/content/library/elements');
      })
      .catch(e => {
        console.error(e);
        setError(ERRORS.DELETE);
        setIsDeleting(false);
      });
  }

  function handleDiscard() {
    history.replace(`/content/library/elements/${elementId}`);
    history.push('/content/library/elements');
  }

  function handlePublish() {
    setIsPublishing(true);
    setError(null);
    request
      .putJSON(`/content/library/elements/${elementId}/publish`, element)
      .then(() => {
        history.replace(`/content/library/elements/${elementId}`);
        history.push('/content/library/elements');
      })
      .catch(e => {
        console.error(e);
        setIsPublishing(false);
        setError(ERRORS.PUBLISH);
      });
  }

  function navigateToEdit() {
    history.replace(`${match.url}/edit`);
  }

  const handleContentChange = useCallback(
    changes => {
      setElement(data => ({
        ...data,
        content: { ...data.content, ...changes },
      }));
    },
    [setElement],
  );

  if (initialFetch.loading) {
    return (
      <div className="ta-c">
        <Spinner size="larger" />
      </div>
    );
  }

  if (initialFetch.error || !element) {
    return (
      <BlankSlate fullWidth>
        <img
          className="mh-300 mb-32"
          src={errorSrc}
          alt={t('common.data_error')}
        />
        <Heading>{t('contents.library.element_library.error.title')}</Heading>
        <Description>
          {t('contents.library.element_library.error.description')}
        </Description>
      </BlankSlate>
    );
  }

  return (
    <Card className="Animated Animated--fadeIn mb-32 d-f f-d-r ff-ss">
      <Card.Content
        padding="larger"
        className="w-260 f-sh-0 border-right border-gray-light"
      >
        <ElementInfo element={element} />
      </Card.Content>
      <Card.Content className="f-1" padding="larger">
        <div className="d-f ai-fs">
          <CSSTransition
            classNames="CSSTransition fade"
            timeout={TRANSITION_TIMEOUT}
            in={!!error}
            unmountOnExit
          >
            <div className="ErrorContainer">
              {error === ERRORS.PUBLISH &&
                t('element_builder.publish_changes.error')}
              {error === ERRORS.DELETE && t('element_builder.delete_error')}
            </div>
          </CSSTransition>
          <ButtonGroup className="ml-a mb-24" alignEnd>
            {!editing && (
              <Button onClick={navigateToEdit}>{t('common.edit')}</Button>
            )}
            {editing && (
              <DiscardChangesButton
                disabled={isPublishing || isDeleting}
                onDiscard={handleDiscard}
              />
            )}
            {editing && (
              <PublishChangesButton
                isLinked={
                  element.saved_element_type === SavedElementTypes.Linked
                }
                loading={isPublishing}
                disabled={isDeleting}
                onPublish={handlePublish}
              />
            )}
            <MoreButton
              isLinked={element.saved_element_type === SavedElementTypes.Linked}
              isPublishing={isPublishing}
              isDeleting={isDeleting}
              onDelete={handleDelete}
            />
          </ButtonGroup>
        </div>
        <div className="EditorElement">
          <ElementRenderer
            element={element}
            onChange={handleContentChange}
            disabled={!editing}
          />
        </div>
      </Card.Content>
    </Card>
  );
};

export default withRouter(ElementEditorRoot);
